<template>
  <v-sheet class="px-3">
    <v-overlay :value="loaderOverlay" :absolute="true">
      <v-row justify="center">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-row>
    </v-overlay>

    <v-row class="header-sticky">
      <v-card min-width="640" class="w-100" :width="'100%'" elevation="0" :color="$vuetify.theme.dark ? '#2E2E2E' : 'grey lighten-3'">
        <v-card-title>
          <v-row justify="start" no-gutters>
            <v-icon large class="pr-2">mdi-chart-box-outline</v-icon>
            <h3>{{ $t("statistics") }}</h3>
          </v-row>
        </v-card-title>

        <v-card-text class="px-3">
          <v-row dense class="mt-2">
            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="periodType"
                :items="periodTypeOptions"
                item-text="text"
                item-value="value"
                :label="$t('kpiPeriodType')"
                dense
                outlined
                hide-details
              />
            </v-col>

            <v-col cols="12" sm="6" md="2">
              <v-menu
                v-if="periodType === 'monthly'"
                v-model="monthMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field :value="monthLabel" :label="$t('kpiPeriodMonth')" dense outlined hide-details readonly v-bind="attrs" v-on="on" />
                </template>
                <v-date-picker v-model="monthPickerModel" type="month" locale="de" first-day-of-week="1" :max="monthPickerMax" @input="monthMenu = false" />
              </v-menu>

              <v-text-field v-else-if="periodType === 'yearly'" dense outlined hide-details disabled value="" :label="''" />

              <v-menu v-else v-model="dateFromMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="dateFromDisplay" :label="$t('kpiFilterDateFrom')" dense outlined hide-details v-bind="attrs" v-on="on" />
                </template>
                <v-date-picker v-model="dateFrom" @input="dateFromMenu = false" locale="de" first-day-of-week="1" />
              </v-menu>
            </v-col>

            <v-col cols="12" sm="6" md="2">
              <v-select
                v-if="periodType === 'monthly' || periodType === 'yearly'"
                v-model="selectedYear"
                :items="yearOptions"
                :label="$t('kpiPeriodYear')"
                dense
                outlined
                hide-details
              />

              <v-menu v-else v-model="dateToMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="dateToDisplay" :label="$t('kpiFilterDateTo')" dense outlined hide-details v-bind="attrs" v-on="on" />
                </template>
                <v-date-picker v-model="dateTo" @input="dateToMenu = false" locale="de" first-day-of-week="1" />
              </v-menu>
            </v-col>

            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="scopeType"
                :items="scopeTypeOptions"
                :item-text="scopeItemText"
                item-value="value"
                :label="$t('kpiFilterScopeLabel')"
                dense
                outlined
                hide-details
              />
            </v-col>

            <v-col cols="12" sm="6" md="2">
              <v-autocomplete
                v-model="scopeTarget"
                :items="scopeTargetOptions"
                item-text="label"
                item-value="value"
                :placeholder="orgUnitPlaceholder"
                dense
                outlined
                hide-details
                :multiple="scopeType === 'employee'"
                :clearable="scopeType !== 'company'"
                :disabled="scopeType === 'company' || (scopeType !== 'company' && scopeTargetOptions.length === 0)"
                class="scope-target-autocomplete"
              />
            </v-col>
          </v-row>

          <v-row class="pa-3">
            <v-col cols="12" sm="6" md="2" class="pa-1 d-flex">
              <ValueKpi :kpi-title="$t('dailySicknessRate')" :kpi-value="currentSicknessRate" kpi-unit="%" :decimals="1" icon="mdi-hospital-box-outline" />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-row>
  </v-sheet>
</template>

<script>
import ValueKpi from "../../components/GeneralReporting/Statistics/ValueKpi.vue";
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

export default {
  name: "Statistics",
  route: "Statistics",
  components: { ValueKpi },

  data() {
    let today = moment().format("YYYY-MM-DD");
    let defaultFrom = moment().subtract(30, "days").format("YYYY-MM-DD");

    return {
      loaderOverlay: false,

      periodType: "monthly",
      selectedMonth: moment().month() + 1,
      selectedYear: moment().year(),

      monthMenu: false,

      dateFrom: defaultFrom,
      dateTo: today,
      dateFromMenu: false,
      dateToMenu: false,

      scopeType: "company",
      scopeTarget: null,

      suppressFetch: false,

      scopeTypeOptions: [
        { value: "company", labelKey: "kpiScopeCompany" },
        { value: "department", labelKey: "kpiScopeDepartment" },
        { value: "employee", labelKey: "kpiScopeEmployee" }
      ],

      currentSicknessRate: null
    };
  },

  computed: {
    ...mapGetters({
      teams: "users/teams",
      employees: "users/employeeList",
      currentUser: "user/user"
    }),

    periodTypeOptions() {
      return [
        { value: "monthly", text: this.$t("kpiPeriodMonthly") },
        { value: "yearly", text: this.$t("kpiPeriodYearly") },
        { value: "range", text: this.$t("kpiPeriodRange") }
      ];
    },

    monthPickerMax() {
      return moment().format("YYYY-MM");
    },

    yearOptions() {
      let currentYear = moment().year();
      let years = [];
      for (let y = currentYear; y >= currentYear - 10; y--) years.push(y);
      return years;
    },

    monthPickerModel: {
      get() {
        let y = String(this.selectedYear || moment().year());
        let m = String(this.selectedMonth || moment().month() + 1).padStart(2, "0");
        return `${y}-${m}`;
      },
      set(val) {
        let s = String(val || "");
        let y = Number(s.slice(0, 4));
        let m = Number(s.slice(5, 7));
        if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return;

        this.suppressFetch = true;
        this.selectedYear = y;
        this.selectedMonth = m;
        this.$nextTick(() => {
          this.suppressFetch = false;
          this.fetchSicknessRate();
        });
      }
    },

    monthLabel() {
      let y = this.selectedYear || moment().year();
      let m = this.selectedMonth || moment().month() + 1;
      let mm = String(m).padStart(2, "0");
      let locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : "de";
      let d = moment(`${y}-${mm}`, "YYYY-MM", true).locale(locale);
      return d.isValid() ? d.format("MMMM") : "";
    },

    effectiveFromDate() {
      if (this.periodType === "monthly") {
        if (!this.selectedYear || !this.selectedMonth) return null;
        let start = moment({ year: this.selectedYear, month: this.selectedMonth - 1, day: 1 });
        return start.isValid() ? start.format("YYYY-MM-DD") : null;
      }

      if (this.periodType === "yearly") {
        if (!this.selectedYear) return null;
        let start = moment({ year: this.selectedYear, month: 0, day: 1 });
        return start.isValid() ? start.format("YYYY-MM-DD") : null;
      }

      if (!this.dateFrom) return null;
      return moment(this.dateFrom, "YYYY-MM-DD").isValid() ? moment(this.dateFrom, "YYYY-MM-DD").format("YYYY-MM-DD") : null;
    },

    effectiveToDate() {
      let today = moment();
      let currentYear = today.year();
      let currentMonth = today.month() + 1;

      if (this.periodType === "monthly") {
        if (!this.selectedYear || !this.selectedMonth) return null;
        let start = moment({ year: this.selectedYear, month: this.selectedMonth - 1, day: 1 });
        if (!start.isValid()) return null;
        if (this.selectedYear === currentYear && this.selectedMonth === currentMonth) return today.format("YYYY-MM-DD");
        return start.clone().endOf("month").format("YYYY-MM-DD");
      }

      if (this.periodType === "yearly") {
        if (!this.selectedYear) return null;
        let start = moment({ year: this.selectedYear, month: 0, day: 1 });
        if (!start.isValid()) return null;
        if (this.selectedYear === currentYear) return today.format("YYYY-MM-DD");
        return start.clone().endOf("year").format("YYYY-MM-DD");
      }

      if (!this.dateTo) return null;
      return moment(this.dateTo, "YYYY-MM-DD").isValid() ? moment(this.dateTo, "YYYY-MM-DD").format("YYYY-MM-DD") : null;
    },

    dateFromDisplay: {
      get() {
        if (!this.dateFrom) return "";
        return moment(this.dateFrom, "YYYY-MM-DD").isValid() ? moment(this.dateFrom, "YYYY-MM-DD").format("DD.MM.YYYY") : "";
      },
      set(value) {
        let m = moment(String(value || ""), ["DD.MM.YYYY", "DDMMYYYY", "DD/MM/YYYY"], true);
        if (m.isValid()) this.dateFrom = m.format("YYYY-MM-DD");
      }
    },

    dateToDisplay: {
      get() {
        if (!this.dateTo) return "";
        return moment(this.dateTo, "YYYY-MM-DD").isValid() ? moment(this.dateTo, "YYYY-MM-DD").format("DD.MM.YYYY") : "";
      },
      set(value) {
        let m = moment(String(value || ""), ["DD.MM.YYYY", "DDMMYYYY", "DD/MM/YYYY"], true);
        if (m.isValid()) this.dateTo = m.format("YYYY-MM-DD");
      }
    },

    orgUnitPlaceholder() {
      if (this.scopeType === "department" && (!this.scopeTarget || String(this.scopeTarget).length === 0)) return this.$t("kpiSelectDepartment");
      if (this.scopeType === "employee") {
        let arr = Array.isArray(this.scopeTarget) ? this.scopeTarget : [];
        if (arr.length === 0) return this.$t("kpiSelectEmployee");
      }
      return "";
    },

    scopeTargetOptions() {
      if (this.scopeType === "company") return [];

      if (this.scopeType === "department") {
        let allLabel = this.$t("all");
        let uniqueNames = Array.from(new Set((this.teams || []).filter((n) => !!n && n !== allLabel)));
        return uniqueNames.map((name) => ({ value: name, label: name }));
      }

      if (this.scopeType === "employee") {
        return (this.employees || [])
          .map((emp) => ({
            value: String(emp.SAPEmpID || emp.SAPEmpId || emp.EmpId),
            label: emp.name || emp.Name
          }))
          .filter((x) => !!x.value);
      }

      return [];
    }
  },

  watch: {
    periodType() {
      if (this.suppressFetch) return;

      let today = moment();
      let currentYear = today.year();
      let currentMonth = today.month() + 1;

      this.suppressFetch = true;
      if (!this.selectedYear) this.selectedYear = currentYear;
      if (!this.selectedMonth) this.selectedMonth = currentMonth;

      if (this.selectedYear === currentYear && this.selectedMonth > currentMonth) this.selectedMonth = currentMonth;

      this.$nextTick(() => {
        this.suppressFetch = false;
        this.fetchSicknessRate();
      });
    },

    selectedMonth() {
      if (this.suppressFetch) return;
      if (this.periodType !== "monthly") return;

      let today = moment();
      let currentYear = today.year();
      let currentMonth = today.month() + 1;

      if (this.selectedYear === currentYear && this.selectedMonth > currentMonth) {
        this.suppressFetch = true;
        this.selectedMonth = currentMonth;
        this.$nextTick(() => {
          this.suppressFetch = false;
          this.fetchSicknessRate();
        });
        return;
      }

      this.fetchSicknessRate();
    },

    selectedYear() {
      if (this.suppressFetch) return;

      let currentYear = moment().year();
      if (this.selectedYear > currentYear) {
        this.suppressFetch = true;
        this.selectedYear = currentYear;
        this.$nextTick(() => {
          this.suppressFetch = false;
          this.fetchSicknessRate();
        });
        return;
      }

      if (this.periodType === "monthly") {
        let currentMonth = moment().month() + 1;
        if (this.selectedYear === currentYear && this.selectedMonth > currentMonth) {
          this.suppressFetch = true;
          this.selectedMonth = currentMonth;
          this.$nextTick(() => {
            this.suppressFetch = false;
            this.fetchSicknessRate();
          });
          return;
        }
      }

      if (this.periodType === "monthly" || this.periodType === "yearly") this.fetchSicknessRate();
    },

    dateFrom() {
      if (this.periodType === "range") this.fetchSicknessRate();
    },

    dateTo() {
      if (this.periodType === "range") this.fetchSicknessRate();
    },

    scopeType() {
      this.suppressFetch = true;
      this.scopeTarget = this.scopeType === "employee" ? [] : null;
      this.$nextTick(() => {
        this.suppressFetch = false;
        this.fetchSicknessRate();
      });
    },

    scopeTarget() {
      if (this.suppressFetch) return;
      this.fetchSicknessRate();
    }
  },

  methods: {
    ...mapActions("reporting", ["getSicknessRate"]),

    scopeItemText(item) {
      return this.$t(item.labelKey);
    },

    async fetchSicknessRate() {
      let fromDate = this.effectiveFromDate;
      let toDate = this.effectiveToDate;

      if (!fromDate || !toDate) return;
      if (moment(fromDate, "YYYY-MM-DD").isAfter(moment(toDate, "YYYY-MM-DD"))) return;

      if (this.scopeType === "department" && (!this.scopeTarget || String(this.scopeTarget).length === 0)) {
        this.currentSicknessRate = null;
        return;
      }

      if (this.scopeType === "employee") {
        let arr = Array.isArray(this.scopeTarget) ? this.scopeTarget : [];
        if (arr.length === 0) {
          this.currentSicknessRate = null;
          return;
        }
      }

      this.loaderOverlay = true;

      try {
        let orgId = null;
        let orgIds = null;

        if (this.scopeType === "department") orgId = this.scopeTarget;

        if (this.scopeType === "employee") {
          let ids = (Array.isArray(this.scopeTarget) ? this.scopeTarget : []).map((x) => String(x)).filter(Boolean);
          orgIds = ids;
        }

        let resp = await this.getSicknessRate({
          fromDate,
          toDate,
          orgLevel: this.scopeType,
          orgId,
          orgIds
        });

        let v = resp?.summary?.sicknessRate;
        this.currentSicknessRate = v === null || v === undefined ? null : Number(v);
      } catch (e) {
        console.error("Error loading sickness rate", e);
        this.currentSicknessRate = null;
      } finally {
        this.loaderOverlay = false;
      }
    }
  },

  created() {
    this.scopeType = "company";
    this.scopeTarget = null;

    Promise.all([this.$store.dispatch("users/getTeams"), this.$store.dispatch("users/getEmployees")]).finally(() => {
      this.fetchSicknessRate();
    });
  }
};
</script>

<style scoped>
.header-sticky {
  min-height: calc(100vh - 110px);
  display: flex;
}
.header-sticky > .v-card {
  flex: 1;
}
::v-deep .scope-target-autocomplete .v-select__slot {
  overflow: hidden;
}

::v-deep .scope-target-autocomplete .v-select__selections {
  flex-wrap: nowrap !important;
  overflow-x: auto;
  overflow-y: hidden;
  align-items: center;
}

::v-deep .scope-target-autocomplete .v-select__selection,
::v-deep .scope-target-autocomplete .v-select__selection--comma {
  white-space: nowrap;
}

::v-deep .scope-target-autocomplete input {
  min-width: 0 !important;
}
</style>
